import { Button, Flex, Row, Space } from "antd";
import * as d3 from "d3";
import { OrgChart } from 'd3-org-chart';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import styles from "../styles/Employee.module.css";
import { toJpeg } from "html-to-image";
import { ReactComponent as DownloadIcon } from "../../../assets/icon/downloadIcon.svg";

function EmployeeEmploymentSubordinates({
  isLoading = false,
  onDownload = () => { },
  graphRef = null
}) {
  const { t, i18n } = useTranslation();
  const subOrdinateList = useSelector((state) => state.employee.subOrdinatesList);
  const supervisor = useSelector((state) => state.employee.supervisorDetails);
  const chartRef = useRef(null);
  const [chart, setChart] = useState(null);

  const downloadImage = async () => {
    const chartElement = document.getElementById('employeeEmploymentSubordinates');

    if (!chartElement) {
      console.error('Element with id "employeeEmploymentSubordinates" not found');
      return;
    }

    const originalWidth = chartElement.style.width;
    const originalHeight = chartElement.style.height;

    chartElement.style.width = `${chartElement.scrollWidth}px`;
    chartElement.style.height = `${chartElement.scrollHeight}px`;

    const scale = 10;
    try {
      const dataUrl = await toJpeg(chartElement, {
        width: chartElement.scrollWidth * scale,
        height: chartElement.scrollHeight * scale,
        backgroundColor: "white",
        style: {
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          width: `${chartElement.scrollWidth}px`,
          height: `${chartElement.scrollHeight}px`,
        },
      });

      chartElement.style.width = originalWidth;
      chartElement.style.height = originalHeight;

      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = 'organizational-chart.jpg';
      link.click();

    } catch (error) {
      console.error('Could not generate image', error);
    }
  };

  const handleDownloadClick = async () => {
    try {
      let res = await chart?.compact?.(false).render().fit().render();
      if (res) {
        await new Promise(resolve => setTimeout(resolve, 3000));
        downloadImage();
      }
    } catch (error) {
      console.error('Error during download:', error);
    }
  };

  function transformData(data, lang) {
    // console.log("companyInformation", companyInformation);

    const result = [
      {
        id: supervisor.empId,
        preventExpand: true
        , parentId: null,
        name: supervisor?.['name' + (lang == 'en' ? "En" : "")] + " " + supervisor?.['surName' + (lang == 'en' ? "En" : "")],
        position: supervisor?.['positionName' + (lang == 'en' ? "En" : "")] || "",
      }];
    function traverse(node, parentId = null) {
      console.log("node", node);

      const transformedNode = {
        id: node.empId || node.id,
        parentId: node.supervisorId || 'root',
        name: node?.['name' + (lang == 'en' ? "En" : "")] + " " + node?.['surName' + (lang == 'en' ? "En" : "")] || node.name,
        position: node?.['positionName' + (lang == 'en' ? "En" : "")] || "",
        image: node.image || '',
        email: node.email || '',
        phone_number: node.phone_number || '',
        hire_date: node.hire_date || '',
        preventExpand: true
      };

      result.push(transformedNode);

      if (node.children && node.children.length > 0) {
        node.children.forEach(child => traverse(child, transformedNode.id));
      }
    }
    data.map(o => traverse(o))
    return result;
  }

  useEffect(() => {
    // Log root data for debugging

    // Check if the root data is valid
    const chartContainer = chartRef?.current;

    // Ensure chart container is available
    if (!chartContainer) {
      console.error("Chart container is not available.");
      return;
    }
    // Initialize OrgChart only after container is available
    const orgChart = new OrgChart()
      .nodeHeight((d) => 50 + 25)
      .nodeWidth((d) => 300 + 2)
      .childrenMargin((d) => 150)
      .compactMarginBetween((d) => 200)
      .compactMarginPair((d) => 200)
      .neighbourMargin((a, b) => 200)

      // .scaleExtent([1, 1])
      // .scaleExtent([0.3, 1])
      // .connectionsUpdate(() => {
      //   d3.select(this)
      //     .attr("stroke", "red") // Set your desired link color
      //     .attr("stroke-width", "2")
      //     .attr("stroke-linecap", "round")
      //     .attr('pointer-events', 'none')
      //     .attr("marker-start", (d) => `url(#${d.from + "_" + d.to})`)
      //     .attr("marker-end", (d) => `url(#arrow-${d.from + "_" + d.to})`);
      // })
      .buttonContent(() => { })
      .onNodeClick((d) => console.log(d))
      .nodeContent(function (d) {
        const color = '#FFFFFF';
        const imageDiffVert = 25 + 2;
        return `
            <div id='node-na' style="width:${d.width}px;height:${d.height}px;padding-left:1px;padding-right:1px;">
              <div style="background-color:${color};margin-left:-1px;width:${d.width - 2}px;border-radius:100px;border: 1px solid black ;padding-left :20px;padding-right :20px;">
                <div style="min-height:${d.height - 20}px;align-content:center;font-size:1.3vw;color:#08011E;text-align:center ;white-space: normal; word-wrap: break-word; overflow: hidden; text-overflow: ellipsis;">${d.data?.name}</div>
                <div style="margin-bottom:15px;align-content:center;font-size:1vw;color:#08011E;text-align:center">${"(" + d.data?.position + ")"}</div>
              </div>
            </div>`;
      })
      .container(chartContainer)
      .data(transformData(subOrdinateList, i18n.language))
      .render();
    // Set chart to state for future manipulation (optional)

    // Styling the connections with custom arrowheads
    const svg = d3.select(chartContainer).select("svg");

    if (svg) {
      // const defs = svg.append("defs");
      // Update connections with the arrowhead
      // .attr("marker-end", "url(#arrowhead)");
      svg.selectAll(".node")
        .style("cursor", "default");
      svg.selectAll("foreignObject").remove()
    }
    orgChart?.expandAll()
    orgChart?.compact?.(false).render();
    // Disable the expand/collapse button specifically
    orgChart?.nodeButtonWidth((node) => node.data?.preventExpand ? 0 : 20)
      .nodeButtonHeight((node) => node.data?.preventExpand ? 0 : 20);
    const rootNodeId = subOrdinateList[0]?.empId; // Assuming the first item is the root
    if (rootNodeId) {
      orgChart.setCentered(rootNodeId);
    }

    const adjustChartSize = () => {
      orgChart.fit();
    };
    setChart(orgChart);
    // Cleanup on component unmount
    return () => {
      if (chart) {
        chart?.destroy?.();
      }
    };
  }, [subOrdinateList, supervisor, i18n.language]);

  useEffect(() => {
    if (graphRef?.current) {
      graphRef.current = chart
    }
  }, [chart])

  return (
    <Space
      style={{ width: "100%", overflow: "auto" }}
      size={24}
      direction="vertical"
    >
      <Row justify="end">
        <Button type="default" disabled={isLoading} onClick={handleDownloadClick}
          style={{ padding: "8px 16px", height: "44px", borderRadius: "8px" }}
        >
          <Flex align="center" justify="center" gap={8}>
            <DownloadIcon />
            <span style={{ color: "#667085", fontWeight: "bold" }}>{t('common.download')}</span>
          </Flex>
        </Button>
      </Row>

      <div>
        <div className={styles.stylesNode2}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px', gap: "12px" }}>
            {/* <Button onClick={() => chart.exportImg({ full: true })} style={{ marginRight: 10 }}>
            Download
          </Button> */}
            <Button onClick={() => {
              chart.nodeHeight(() => 125 + 25);
              chart.layout('left').render().fit()
            }}>
              {t("og_chart.left")}
            </Button>
            <Button onClick={() => {
              chart.nodeHeight(() => 50 + 25);
              chart.layout('top').render().fit()
            }}>
              {t("og_chart.top")}
            </Button>
          </div>
          <div id="employeeEmploymentSubordinates" style={{ width: "100%" }} ref={chartRef} className="chart-container"></div>
        </div>
      </div>
    </Space>
  );

  //   return data.map((node) => (
  //     <div id="chart">
  //       <Tree
  //         nodePadding="24px"
  //         lineHeight="19px"
  //         label={
  //           <div className={styles.stylesNode}>
  //             {lang == "en" ? node?.positionNameEn : node?.positionName}
  //           </div>
  //         }
  //       >
  //         {node?.children?.length > 0 ? renderTreeNodes(node?.children, lang) : null}
  //       </Tree>
  //     </div>
  //   ));

};

export default EmployeeEmploymentSubordinates;